import { ButtonHeading, CommonDatePicker, CommonInput, CommonTextarea, CustomPagination, CustomSelect } from 'components'
import { CityDropdownForTrip } from 'components'
import React, { useCallback, useContext, useEffect, useState } from 'react'
import './addPayRec.css'
import { FiFilter } from 'react-icons/fi';
import { IoWarningOutline } from "react-icons/io5";
import { UserContext } from 'context';
import StorageService from 'utils/StorageServices/StorageServices';
import { venderDropdownApi, getDriverDropdownListData, getUserAccountList } from '_services';
import { getSupplierListData } from '_services/accountsServices/supplierServices';
import { getConsignorListData } from '_services/accountsServices/consignorServices';
import { getFuelApi } from '_services/dropdownServices/dropdownService';
import { getStaffListData } from '_services/accountsServices/staffServices';
import { getConsignorBillRaisePaymentList, addPaymentReceivedMoneyInApi } from '_services/accountingServices/accountingServices';
import toast from 'react-hot-toast';
import { errorSchema, utils } from 'helper';
import { useNavigate } from 'react-router-dom';

const AddPaymentReceipt = () => {

    const { masterData } = useContext(UserContext);
    const navigate = useNavigate();
    const [accountList, setAccountList] = useState([]);
    const [accountLoading, setAccountLoading] = useState(false);
    const [invoiceList, setInvoiceList] = useState([]);
    const [listLoading, setListLoading] = useState(false);
    const [totalRecords, setTotalRecords] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [search, setSearch] = useState("");
    const [submitLoading, setSubmitLoading] = useState(false);
    const [showAmountPopover, setShowAmountPopover] = useState(false);
    const [payFullChecked, setPayFullChecked] = useState(false);
    const [formError, setFormError] = useState("");
    const limit = 10;

    const [form, setForm] = useState({
        vendor_type: 2,
        vendor_id: {},
        branch_id: {},
        payment_mode: null,
        account_id: "",
        tds_type: 1,
        payment_amount: "",
        reference: "",
        notes: "",
        voucher: "",
        date: new Date(),
    });

    const paymentModeOptions = (masterData?.account_category?.sub_category || [])
        .filter((item) => [2, 3, 14].includes(item?.id))
        .map((item) => ({ label: item?.name, value: item?.id }));

    const applyAutoFill = (amount) => {
        if (!amount || Number(amount) <= 0) return;
        let remaining = Number(amount);
        const updated = invoiceList.map((item) => {
            const balance = Number(item?.invoice_summary?.balance) || 0;
            const pay = Math.min(balance, remaining);
            remaining = Math.max(0, remaining - pay);
            return { ...item, payment: pay > 0 ? String(pay) : "" };
        });
        setInvoiceList(updated);
    };

    // ─── handleOnChange (OtherOwnModal jaisa — real-time validation) ─────────────
    const handleOnChange = async (name, value) => {
        const stateObj = { ...form, [name]: value };
        setForm(stateObj);

        if (!!formError) {
            const validationResult = await utils.checkFormError(
                { ...stateObj },
                errorSchema.addPaymentReceiptSchema
            );
            if (validationResult === true) {
                setFormError("");
            } else {
                setFormError(validationResult);
            }
        }
    };

    // ─── Vendor fetch functions ──────────────────────────────────────────────────
    const fetchSupplier = useCallback(async (search, page, limit) => {
        try {
            const res = await getSupplierListData({
                company_id: StorageService.getCompanyId(),
                offset: page, limit, search,
                firm_id: form?.branch_id?.firm_id?._id || StorageService.getFirmId(),
            });
            if (res?.responseCode === 200) {
                const list = res.data.supplier_list.map((i) => ({ ...i, label: i.name, value: i._id }));
                return { items: list, total: res.data.total_record || list.length };
            }
            return { items: [], total: 0 };
        } catch { return { items: [], total: 0 }; }
    }, [form?.branch_id?.value]);

    const fetchParty = useCallback(async (search, page, limit) => {
        try {
            const res = await getConsignorListData({
                company_id: StorageService.getCompanyId(),
                offset: page, limit, search,
                firm_id: form?.branch_id?.firm_id?._id || StorageService.getFirmId(),
            });
            if (res?.responseCode === 200) {
                const list = res.data.consignor_list.map((i) => ({ ...i, label: i.name, value: i._id }));
                return { items: list, total: res.data.total_record || list.length };
            }
            return { items: [], total: 0 };
        } catch { return { items: [], total: 0 }; }
    }, [form?.branch_id?.value]);

    const fetchDriver = useCallback(async (search, page, limit) => {
        try {
            const res = await getDriverDropdownListData({
                company_id: StorageService.getCompanyId(),
                offset: page, limit, search,
                driver_type: [1, 3],
                firm_id: form?.branch_id?.firm_id?._id || StorageService.getFirmId(),
            });
            if (res?.responseCode === 200) {
                const list = res.data.driver_list.map((i) => ({ ...i, label: i.name, value: i._id }));
                return { items: list, total: res.data.total_record || list.length };
            }
            return { items: [], total: 0 };
        } catch { return { items: [], total: 0 }; }
    }, [form?.branch_id?.value]);

    const fetchVendor = useCallback(async (search, page, limit) => {
        try {
            const res = await venderDropdownApi({
                company_id: StorageService.getCompanyId(),
                offset: page, limit, search,
                firm_id: form?.branch_id?.firm_id?._id || StorageService.getFirmId(),
            });
            if (res?.responseCode === 200) {
                const list = res.data.vendor_list.map((i) => ({ ...i, label: i.name, value: i._id }));
                return { items: list, total: res.data.total_record || list.length };
            }
            return { items: [], total: 0 };
        } catch { return { items: [], total: 0 }; }
    }, [form?.branch_id?.value]);

    const fetchFuelStation = useCallback(async (search, page, limit) => {
        try {
            const res = await getFuelApi({
                company_id: StorageService.getCompanyId(),
                offset: page, limit, search,
                firm_id: form?.branch_id?.firm_id?._id || StorageService.getFirmId(),
            });
            if (res?.responseCode === 200) {
                const list = res.data.fuelstation_list.map((i) => ({ ...i, label: i.name, value: i._id }));
                return { items: list, total: res.data.total_record || list.length };
            }
            return { items: [], total: 0 };
        } catch { return { items: [], total: 0 }; }
    }, [form?.branch_id?.value]);

    const fetchStaff = useCallback(async (search, page, limit) => {
        try {
            const res = await getStaffListData({
                company_id: StorageService.getCompanyId(),
                offset: page, limit, search,
                firm_id: form?.branch_id?.firm_id?._id || StorageService.getFirmId(),
            });
            if (res?.responseCode === 200) {
                const list = res.data.list.map((i) => ({ ...i, label: i.name, value: i._id }));
                return { items: list, total: res.data.total_record || list.length };
            }
            return { items: [], total: 0 };
        } catch { return { items: [], total: 0 }; }
    }, [form?.branch_id?.value]);

    const listMap = {
        1: { placeholder: "Select Supplier", fetchData: fetchSupplier },
        2: { placeholder: "Select Party", fetchData: fetchParty },
        3: { placeholder: "Select Driver", fetchData: fetchDriver },
        4: { placeholder: "Select Vendor", fetchData: fetchVendor },
        5: { placeholder: "Select Fuel Station", fetchData: fetchFuelStation },
        8: { placeholder: "Select Staff", fetchData: fetchStaff },
    };

    const activeVendorConfig = listMap[form?.vendor_type] || { placeholder: "Select Vendor", fetchData: fetchVendor };

    const vendorTypeSelectOptions = (masterData?.vendor_type_list || [])
        .filter((item) => ![6, 7].includes(item?.id))
        .map((item) => ({ label: item?.name, value: item?.id }));

    // ─── Effects ─────────────────────────────────────────────────────────────────
    useEffect(() => {
        if (form?.payment_mode) getAccountListHandler();
    }, [form?.payment_mode]);

    useEffect(() => {
        if (form?.vendor_id?.value) {
            setCurrentPage(1);
            setSearch("");
            fetchInvoiceList(1, "");
        } else {
            setInvoiceList([]);
            setTotalRecords(0);
        }
    }, [form?.vendor_id?.value]);

    useEffect(() => {
        if (!form?.vendor_id?.value) return;
        const timer = setTimeout(() => {
            setCurrentPage(1);
            fetchInvoiceList(1, search);
        }, 400);
        return () => clearTimeout(timer);
    }, [search]);

    // ─── API handlers ────────────────────────────────────────────────────────────
    const getAccountListHandler = async () => {
        setAccountLoading(true);
        try {
            const res = await getUserAccountList({
                company_id: StorageService.getCompanyId(),
                category_id: 1,
                sub_category_id: parseInt(form?.payment_mode),
                firm_id: form?.branch_id?.firm_id?._id || StorageService.getFirmId(),
            });
            if (res?.responseCode === 200) {
                const list = res.data.account_list.map((i) => ({ ...i, label: i.name, value: i._id }));
                setAccountList(list);
                setForm((s) => {
                    const updated = { ...s, account_id: list[0]?.value || "" };
                    // ✅ account_id set hone pe formError clear karo
                    if (formError?.account_id) {
                        setFormError((prev) => ({ ...prev, account_id: "" }));
                    }
                    return updated;
                });
            } else {
                setAccountList([]);
            }
        } catch { setAccountList([]); }
        finally { setAccountLoading(false); }
    };

    const fetchInvoiceList = async (page = 1, searchVal = "") => {
        if (!form?.vendor_id?.value) return;
        setListLoading(true);
        try {
            const res = await getConsignorBillRaisePaymentList({
                company_id: StorageService.getCompanyId(),
                consignor_id: form?.vendor_id?.value,
                limit,
                offset: page,
                search: searchVal,
            });
            if (res?.responseCode === 200) {
                setInvoiceList(res?.data?.list || []);
                setTotalRecords(res?.data?.total_record || 0);
            } else {
                setInvoiceList([]);
                setTotalRecords(0);
            }
        } catch { setInvoiceList([]); setTotalRecords(0); }
        finally { setListLoading(false); }
    };

    const handlePageChange = (page) => {
        setCurrentPage(page);
        fetchInvoiceList(page, search);
    };

    // ─── Invoice row change ──────────────────────────────────────────────────────
    const handleInvoiceChange = (index, field, value) => {
        const updated = [...invoiceList];
        const item = updated[index];

        if (field === "tds" && form?.tds_type === 2) {
            const otherTds = invoiceList.reduce((sum, i, idx) =>
                idx !== index ? sum + (Number(i.tds) || 0) : sum, 0);
            const maxTdsForRow = Math.min(
                Number(item?.invoice_summary?.balance) || 0,
                (Number(form?.payment_amount) || 0) - otherTds
            );
            if (Number(value) > maxTdsForRow) value = String(Math.max(0, maxTdsForRow));
        }

        if (field === "payment") {
            const otherPayments = invoiceList.reduce((sum, i, idx) =>
                idx !== index ? sum + (Number(i.payment) || 0) : sum, 0);
            const remainingBudget = (Number(form?.payment_amount) || 0) - otherPayments;
            const maxPayment = Math.min(
                Number(item?.invoice_summary?.balance) || 0,
                remainingBudget
            );
            if (Number(value) > maxPayment) value = String(Math.max(0, maxPayment));
        }

        updated[index][field] = value;
        setInvoiceList(updated);

        // ✅ Payment enter hone pe invoiceList error clear karo
        if (field === "payment" && Number(value) > 0 && formError?.invoiceList) {
            setFormError((prev) => ({ ...prev, invoiceList: "" }));
        }
    };

    // ─── Pay in Full ─────────────────────────────────────────────────────────────
    const handlePayInFull = (index) => {
        const updated = [...invoiceList];
        const item = updated[index];
        const otherPayments = invoiceList.reduce((sum, i, idx) =>
            idx !== index ? sum + (Number(i.payment) || 0) : sum, 0);
        const remainingBudget = (Number(form?.payment_amount) || 0) - otherPayments;
        const maxPayment = Math.min(
            Number(item?.invoice_summary?.balance) || 0,
            Math.max(0, remainingBudget)
        );
        updated[index].payment = String(maxPayment);
        setInvoiceList(updated);

        // ✅ error clear karo
        if (formError?.invoiceList) {
            setFormError((prev) => ({ ...prev, invoiceList: "" }));
        }
    };

    // ─── Summary computed values ─────────────────────────────────────────────────
    const totalTds = form?.tds_type === 2
        ? invoiceList.reduce((sum, i) => sum + (Number(i.tds) || 0), 0)
        : 0;
    const totalPayment = invoiceList.reduce((sum, i) => sum + (Number(i.payment) || 0), 0);
    const amountReceived = Number(form?.payment_amount) || 0;
    const netAmount = amountReceived;
    const amountUsed = totalPayment;
    const excess = netAmount - amountUsed;

    // ─── Submit ──────────────────────────────────────────────────────────────────
    const handleSubmit = async () => {
        try {
            // ✅ Step 1 — Form fields validate karo
            const validationResult = await utils.checkFormError(
                { ...form },
                errorSchema.addPaymentReceiptSchema
            );

            if (validationResult !== true) {
                setFormError(validationResult);
                return;
            }

            // ✅ Step 2 — Invoice list check karo
            if (invoiceList.length === 0) {
                setFormError((prev) => ({
                    ...prev,
                    invoiceList: "Please select a party to load invoices",
                }));
                return;
            }

            // ✅ Step 3 — At least 1 payment entered karo
            const hasPayment = invoiceList.some((i) => Number(i.payment) > 0);
            if (!hasPayment) {
                setFormError((prev) => ({
                    ...prev,
                    invoiceList: "Please enter payment amount for at least one invoice",
                }));
                return;
            }

            setFormError("");
            setSubmitLoading(true);

            const items = invoiceList
                .filter((i) => Number(i.payment) > 0)
                .map((i) => ({
                    billraise_id: i._id,
                    tds: form?.tds_type === 2 ? String(i.tds || "0") : "0",
                    payment: String(i.payment || "0"),
                    invoice_number: i.invoice_number || "",
                }));

            const payload = {
                company_id: StorageService.getCompanyId(),
                date: form?.date ? new Date(form.date).toISOString() : "",
                voucher: form?.voucher || "",
                vendor_type: form?.vendor_type,
                vendor_id: form?.vendor_id?.value || "",
                account_category: 1,
                account_sub_category: form?.payment_mode || "",
                account_id: form?.account_id || "",
                reference: form?.reference || "",
                payment_amount: String(form?.payment_amount || "0"),
                is_tds: form?.tds_type === 2,
                items,
                amount_received: String(amountReceived.toFixed(2)),
                net_amount: String(netAmount.toFixed(2)),
                amount_used_for_payment: String(amountUsed.toFixed(2)),
                amount_in_excess: String(excess.toFixed(2)),
            };

            const res = await addPaymentReceivedMoneyInApi(payload);
            if (res?.responseCode === 200) {
                toast.success(res?.responseMessage || "Payment saved successfully");
                setForm({
                    vendor_type: 2, vendor_id: {}, branch_id: {},
                    payment_mode: null, account_id: "", tds_type: 1,
                    payment_amount: "", reference: "", notes: "",
                    voucher: "", date: new Date(),
                });
                setInvoiceList([]);
                setTotalRecords(0);
                setCurrentPage(1);
                setSearch("");
                navigate("/accounting/moneyin");
            }
        } catch { toast.error("Something went wrong"); }
        finally { setSubmitLoading(false); }
    };

    return (
        <>
            <div className='__addPayRec_mainBx'>
                <ButtonHeading title="Add Payment Receipt" />

                <div className='__addPayRec_frmBx'>
                    <div className='__addPayRec_allBxs'>
                        <div className='__addPayRec_inupBx'>

                            {/* Payment Date */}
                            <div className='__addPayRec_dualFx'>
                                <CommonDatePicker
                                    label="Payment Date"
                                    startDate={form?.date}
                                    setStartDate={(val) => setForm((s) => ({ ...s, date: val }))}
                                    maxDate={new Date()}
                                />
                            </div>

                            {/* Payment Voucher — no auto-generate */}
                            <div className='__addPayRec_dualFx'>
                                <CommonInput
                                    placeholder='Payment Voucher'
                                    label='Payment Voucher'
                                    value={form?.voucher || ""}
                                    onChange={({ target: { value } }) =>
                                        setForm((s) => ({ ...s, voucher: value }))
                                    }
                                />
                            </div>

                            {/* Vendor Type */}
                            <div className='__addPayRec_dualFx'>
                                <CustomSelect
                                    placeholder='Vendor Type'
                                    label='Vendor Type'
                                    options={vendorTypeSelectOptions}
                                    value={vendorTypeSelectOptions.find((i) => i.value === form?.vendor_type) || null}
                                    onSelect={(data) => setForm((s) => ({
                                        ...s, vendor_type: data?.value, vendor_id: {},
                                    }))}
                                    disabled={true}
                                />
                            </div>

                            {/* Party / Vendor dropdown */}
                            <div className='__addPayRec_dualFx'>
                                <CityDropdownForTrip
                                    key={`${form?.vendor_type}_${form?.branch_id?.value}`}
                                    label={activeVendorConfig.placeholder}
                                    placeholder={activeVendorConfig.placeholder}
                                    fetchData={activeVendorConfig.fetchData}
                                    pageSize={20}
                                    onChange={(data) => handleOnChange("vendor_id", data)}
                                    selectedItem={form?.vendor_id?.value ? form?.vendor_id : null}
                                    error={!!formError?.vendor_id}
                                    errorMessage={formError?.vendor_id}
                                />
                            </div>

                            {/* Payment Mode */}
                            <div className='__addPayRec_dualFx'>
                                <CustomSelect
                                    placeholder='Payment Mode'
                                    label='Payment Mode'
                                    options={paymentModeOptions}
                                    value={paymentModeOptions.find((i) => i.value === form?.payment_mode) || null}
                                    onSelect={(data) => handleOnChange("payment_mode", data?.value)}
                                    focus={!!formError?.payment_mode}
                                    error={formError?.payment_mode}
                                />
                            </div>

                            {/* Deposited To */}
                            <div className='__addPayRec_dualFx'>
                                <CustomSelect
                                    placeholder='Deposited To'
                                    label='Deposited To'
                                    options={accountList}
                                    value={accountList.find((i) => i.value === form?.account_id) || null}
                                    onSelect={(data) => handleOnChange("account_id", data?.value)}
                                    isLoading={accountLoading}
                                    focus={!!formError?.account_id}
                                    error={formError?.account_id}
                                />
                            </div>

                            {/* Reference */}
                            <div className='__addPayRec_dualFx'>
                                <CommonInput
                                    placeholder='Reference'
                                    label='Reference'
                                    maxLength={20}
                                    value={form?.reference || ""}
                                    onChange={({ target: { value } }) => {
                                        const clean = value.replace(/[^a-zA-Z0-9]/g, "").slice(0, 20);
                                        setForm((s) => ({ ...s, reference: clean }));
                                    }}
                                />
                            </div>

                            {/* Payment Amount */}
                            <div className='__addPayRec_dualFx' style={{ position: "relative" }}>
                                <CommonInput
                                    placeholder='Payment Amount'
                                    label='Payment Amount'
                                    type="number"
                                    value={form?.payment_amount || ""}
                                    onChange={({ target: { value } }) => {
                                        if (/^\d*(\.\d{0,2})?$/.test(value)) {
                                            setPayFullChecked(false);
                                            setShowAmountPopover(false);
                                            setInvoiceList((prev) => prev.map((i) => ({ ...i, payment: "" })));
                                            handleOnChange("payment_amount", value);  // ← ye use karo
                                        }
                                    }}
                                    onBlur={() => {
                                        setTimeout(() => {
                                            if (Number(form?.payment_amount) > 0 && invoiceList.length > 0) {
                                                setShowAmountPopover(true);
                                            }
                                        }, 150);
                                    }}
                                    focus={!!formError?.payment_amount}
                                    error={formError?.payment_amount}
                                />

                                {Number(form?.payment_amount) > 0 && invoiceList.length > 0 && (
                                    <div style={{ position: "relative", marginTop: 6 }}>

                                        {showAmountPopover && (
                                            <div style={{
                                                position: "absolute",
                                                left: 0,
                                                zIndex: 999,
                                                background: "#fff",
                                                border: "1px solid #e0e0e0",
                                                borderRadius: 8,
                                                boxShadow: "0 4px 16px rgba(0,0,0,0.10)",
                                                padding: "14px 16px",
                                                minWidth: 300,
                                            }}>
                                                <p style={{ fontSize: 13, margin: "0 0 12px", color: "#333" }}>
                                                    Would you like this amount to be reflected in the Payment field?
                                                </p>
                                                <div style={{ display: "flex", gap: 8 }}>
                                                    <button
                                                        className="anime-btn _addComm_btn"
                                                        style={{ padding: "5px 18px", fontSize: 13 }}
                                                        onMouseDown={(e) => {
                                                            e.preventDefault();
                                                            applyAutoFill(form?.payment_amount);
                                                            setPayFullChecked(true);
                                                            setShowAmountPopover(false);
                                                        }}
                                                    >
                                                        Yes
                                                    </button>
                                                    <button
                                                        className="anime-btn _cancel_btn"
                                                        style={{ padding: "5px 18px", fontSize: 13 }}
                                                        onMouseDown={(e) => {
                                                            e.preventDefault();
                                                            setShowAmountPopover(false);
                                                        }}
                                                    >
                                                        No
                                                    </button>
                                                </div>
                                            </div>
                                        )}

                                        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                            <input
                                                type="checkbox"
                                                id="payFullCb"
                                                checked={payFullChecked}
                                                onChange={(e) => {
                                                    const checked = e.target.checked;
                                                    setPayFullChecked(checked);
                                                    if (checked) {
                                                        applyAutoFill(form?.payment_amount);
                                                    } else {
                                                        setInvoiceList((prev) =>
                                                            prev.map((i) => ({ ...i, payment: "" }))
                                                        );
                                                    }
                                                }}
                                            />
                                            <label htmlFor="payFullCb" style={{ fontSize: 13, cursor: "pointer" }}>
                                                Pay full amount (₹{Number(form?.payment_amount).toLocaleString("en-IN", { minimumFractionDigits: 2 })})
                                            </label>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* ─── Tax Deduction ───────────────────────────────────────── */}
                        <div className='__txDeductDe'>
                            <p>Tax Deducted?</p>
                            <div className='__txDeductFlxs'>
                                {[
                                    { label: "No, Tax Deducted", value: 1 },
                                    { label: "Yes, TDS (Income Tax)", value: 2 },
                                ].map((i) => (
                                    <div className="_dualCheckr_bx" key={i?.value}>
                                        <input
                                            className="form-check-input"
                                            id={i?.label}
                                            type="radio"
                                            name="tds_type"
                                            value={i.value}
                                            checked={form?.tds_type === i.value}
                                            onChange={() => setForm((s) => ({ ...s, tds_type: i.value }))}
                                        />
                                        <label htmlFor={i?.label} className="form-check-label">
                                            {i?.label}
                                        </label>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* ─── Unpaid Invoices ─────────────────────────────────────── */}
                        <div className='__unpaidInvBx'>
                            <div className='__unpaidHead'><p>Unpaid Invoices</p></div>

                            <div className='__unpaid_searchFilter'>
                                <CommonInput
                                    placeholder='Search Invoices'
                                    label='Search Invoices'
                                    value={search}
                                    onChange={({ target: { value } }) => setSearch(value)}
                                />
                                <button className='__unpaid_filterBtn'>
                                    <FiFilter /> <span>Filters</span>
                                </button>
                            </div>

                            <div className="__unPaid_tableWrapper">
                                <table className="__unPaid_customTable">
                                    <thead>
                                        <tr>
                                            <th>Date</th>
                                            <th>Invoice Number</th>
                                            <th>Total Amount</th>
                                            <th>Amount Due</th>
                                            {form?.tds_type === 2 && <th>TDS</th>}
                                            <th>Payment</th>
                                            <th></th>
                                        </tr>
                                    </thead>

                                    <tbody>
                                        {listLoading ? (
                                            <tr>
                                                <td colSpan={form?.tds_type === 2 ? 7 : 6}
                                                    style={{ textAlign: "center", padding: 20 }}>
                                                    Loading...
                                                </td>
                                            </tr>
                                        ) : invoiceList.length === 0 ? (
                                            <tr>
                                                <td colSpan={form?.tds_type === 2 ? 7 : 6}
                                                    style={{ textAlign: "center", padding: 20 }}>
                                                    No invoices found
                                                </td>
                                            </tr>
                                        ) : (
                                            invoiceList.map((item, index) => (
                                                <tr key={index}>
                                                    <td>
                                                        <div>{utils?.formatDate(item?.invoice_date)}</div>
                                                        <small>Due Date: {utils?.formatDate(item?.due_date)}</small>
                                                    </td>

                                                    <td>{item?.invoice_number}</td>

                                                    <td>₹ {Number(item?.invoice_summary?.total_freight || 0).toLocaleString()}</td>

                                                    <td>₹ {Number(item?.invoice_summary?.balance || 0).toLocaleString()}</td>

                                                    {form?.tds_type === 2 && (
                                                        <td>
                                                            <CommonInput
                                                                placeholder="Enter Amount"
                                                                className="tableInput"
                                                                value={item.tds || ""}
                                                                onChange={(e) =>
                                                                    handleInvoiceChange(index, "tds", e.target.value)
                                                                }
                                                            />
                                                        </td>
                                                    )}

                                                    <td>
                                                        <CommonInput
                                                            type="text"
                                                            placeholder="Enter Amount"
                                                            className="tableInput"
                                                            value={item.payment || ""}
                                                            onChange={(e) =>
                                                                handleInvoiceChange(index, "payment", e.target.value)
                                                            }
                                                        />
                                                    </td>

                                                    <td>
                                                        <button
                                                            className="payLink"
                                                            style={{
                                                                background: "none",
                                                                border: "none",
                                                                padding: 0,
                                                                cursor: "pointer",
                                                                fontSize: 13,
                                                                color: "#1a73e8",
                                                            }}
                                                            onClick={() => handlePayInFull(index)}
                                                        >
                                                            Pay in Full
                                                        </button>
                                                    </td>
                                                </tr>
                                            ))
                                        )}

                                        {invoiceList.length > 0 && (
                                            <tr className="totalRow">
                                                <td colSpan="3"></td>
                                                <td>Total</td>
                                                {form?.tds_type === 2 && (
                                                    <td>₹ {totalTds.toLocaleString()}</td>
                                                )}
                                                <td>₹ {totalPayment.toLocaleString()}</td>
                                                <td></td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>

                                {/* ✅ Invoice error */}
                                {formError?.invoiceList && (
                                    <p style={{ color: "red", fontSize: 12, marginTop: 6, fontWeight: 500 }}>
                                        {formError.invoiceList}
                                    </p>
                                )}

                                <div className='paginationFt_avt'>
                                    <CustomPagination
                                        totalItems={totalRecords}
                                        itemsPerPage={limit}
                                        currentPage={currentPage}
                                        onPageChange={handlePageChange}
                                    />
                                </div>
                            </div>
                            
                        </div>

                        {/* ─── Claims ──────────────────────────────────────────────── */}
                        <div className='__clainDeductBx'>
                            <div className='__clainDeductHead'><p>Add Claims Deductions</p></div>
                            <div>
                                <div className="noClaimsWrapper">
                                    <h3>No Claims Selected</h3>
                                    <p>Click the button below to select Claims for payment</p>
                                    <button className="selectClaim_btn">
                                        + Select Claims (0 available)
                                    </button>
                                </div>
                                <div className='paginationFt_avt'>
                                    <CustomPagination totalItems={2} itemsPerPage={12} />
                                </div>
                            </div>
                        </div>

                        {/* ─── Payment Summary ─────────────────────────────────────── */}
                        <div className='__pr_paymentSummary_mainBx'>
                            <div className="__pr_paymentSummary">
                                <div className="__pr_rowItem">
                                    <span className='__pr_semibold'>Amount Received :</span>
                                    <span>₹ {amountReceived.toFixed(2)}</span>
                                </div>

                                <div className="__pr_rowItem">
                                    <span className='__pr_semibold'>Net Amount :</span>
                                    <span className="__pr_bold">₹ {netAmount.toFixed(2)}</span>
                                </div>

                                <div className="__pr_rowItem">
                                    <span className='__pr_semibold'>Amount used for Payments :</span>
                                    <span>₹ {amountUsed.toFixed(2)}</span>
                                </div>

                                <div className="__pr_rowItem __pr_danger">
                                    <span><IoWarningOutline /> Amount in Excess:</span>
                                    <span>₹ {excess.toFixed(2)}</span>
                                </div>

                                <div className="__pr_infoBox">
                                    💡 Excess amount will be available for adjustment in future payments
                                </div>
                            </div>
                        </div>

                        {/* ─── Notes ───────────────────────────────────────────────── */}
                        <div className='__notesBx'>
                            <span>Notes (Internal use. Not Visible to Customer)</span>
                            <CommonTextarea
                                value={form?.notes || ""}
                                onChange={({ target: { value } }) =>
                                    setForm((s) => ({ ...s, notes: value }))
                                }
                            />
                        </div>
                    </div>
                </div>

                <div className="bottomFix">
                    <button
                        className='anime-btn _addComm_btn _sameWid'
                        onClick={handleSubmit}
                        disabled={submitLoading}
                    >
                        {submitLoading ? "Saving..." : "Save"}
                    </button>
                    <button className='anime-btn _cancel_btn _sameWid'>Cancel</button>
                </div>
            </div>
        </>
    );
};

export default AddPaymentReceipt;